import React, { useState } from 'react';
import { makeStyles, fade } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import SearchIcon from '@material-ui/icons/SearchRounded';
import InputBase from '@material-ui/core/InputBase';
import Fuse from 'fuse.js';
import { withSnackbar } from 'notistack';
import { FixedSizeList } from 'react-window';

import LoadingButton from '../../Buttons/Loading';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    width: 350,
    height: 400,
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto'
  },
  button: {
    margin: theme.spacing(0.5, 0),
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: fade(theme.palette.common.black, 0.15),
    '&:hover': {
      backgroundColor: fade(theme.palette.common.black, 0.25),
    },
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(3),
      width: 'auto',
    },
  },
  searchIcon: {
    width: theme.spacing(7),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  grow: {
    flexGrow: 1,
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 7),
    width: '100%',
  }
}));

const not = (a, b) => {
  return a.filter(value => b.indexOf(value) === -1);
}

const intersection = (a, b) => {
  return a.filter(value => b.indexOf(value) !== -1);
}

const union = (a, b) => {
  return [...a, ...not(b, a)];
}

const EnhancferList = ({ 
  isOpen, 
  onClose,
  onExited = () => null,
  data={}, 
  initLeft=[], 
  initRight=[], 
  title='', 
  leftLabel='Choices', 
  rightLabel='Chosen',
  valueExtractor=() => {},
  searchKeys=[],
  searchId='',
  onSaveHandler=() => {},
  saveButtonText='Save',
  loading = false,
  enqueueSnackbar,
  limit
}) => {
  const classes = useStyles();
  const [search, setSearch] = useState({ left: '', right: '' });
  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState(initLeft);
  const [right, setRight] = useState(initRight);
  const [fuse, setFuse] = useState({ left: {}, right: {} });
  const [temp, setTemp] = useState({ left: [], right: [] });
  const [prevSearch, setPrevSearch] = useState({ left: '', right: '' });

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = value => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = ids => intersection(checked, ids).length;

  const handleToggleAll = items => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    if(limit && right.concat(leftChecked).length > limit) 
      return enqueueSnackbar(`You can only add a max of ${limit} items to the right side.`, { variant: 'error' });
    if(search.left !== '') {
      setSearch({...search, left: ''});
      setLeft(not(temp.left, leftChecked));
      setTemp({...temp, left: not(temp.left, leftChecked)});
    } else {
      setLeft(not(left, leftChecked));
    }
    setChecked(not(checked, leftChecked));
    setRight(right.concat(leftChecked));
  };

  const handleCheckedLeft = () => {
    if(search.right !== '') {
      setSearch({...search, right: ''});
      setRight(not(temp.right, rightChecked));
      setTemp({...temp, right: not(temp.right, rightChecked)});
    } else {
      setRight(not(right, rightChecked));
    }
    setChecked(not(checked, rightChecked));
    setLeft(left.concat(rightChecked));
  };

  const fuseOptions = {
    shouldSort: true,
    includeScore: true,
    threshold: 0.32,
    location: 0,
    distance: 50,
    maxPatternLength: 32,
    minMatchCharLength: 3,
    keys: searchKeys,
    id: searchId
  };

  const createSearchIndices = side => {
    if(side === 'left') setFuse({...fuse, left: new Fuse(left.map(id => data[id]), fuseOptions)});
    else if (side === 'right') setFuse({...fuse, right: new Fuse(right.map(id => data[id]), fuseOptions)});
  }

  const LeftRow = ({ index, style }) => {
    const id = left[index];
    const value = valueExtractor(data[id]);
    const labelId = `transfer-list-all-item-${value}-label`;
    return (
      <ListItem key={id} role="listitem" style={style} button onClick={handleToggle(id)}>
        <ListItemIcon>
          <Checkbox
            checked={checked.indexOf(id) !== -1}
            tabIndex={-1}
            disableRipple
            inputProps={{ 'aria-labelledby': labelId }}
          />
        </ListItemIcon>
        <ListItemText id={labelId} primary={value} />
      </ListItem>
    );
  }

  const RightRow = ({ index, style }) => {
    const id = right[index];
    const value = valueExtractor(data[id]);
    const labelId = `transfer-list-all-item-${value}-label`;
    return (
      <ListItem key={id} role="listitem" style={style} button onClick={handleToggle(id)}>
        <ListItemIcon>
          <Checkbox
            checked={checked.indexOf(id) !== -1}
            tabIndex={-1}
            disableRipple
            inputProps={{ 'aria-labelledby': labelId }}
          />
        </ListItemIcon>
        <ListItemText id={labelId} primary={value} />
      </ListItem>
    );
  }

  const RightList = (title) => (
    <Card>
      <div className={classes.search}>
        <div className={classes.searchIcon}>
          <SearchIcon />
        </div>
        <InputBase
          placeholder="Search…"
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
          }}
          value={search.right}
          inputProps={{ 'aria-label': 'search' }}
          onChange={
            e => {
              const value = e.target.value;
              setSearch({...search, right: value});
              if(prevSearch.right === '' && value !== '') setTemp({ ...temp, right })
              if(value !== '') setRight(Object.values(fuse.right.search(value)).map(item => item.item))
              else setRight(temp.right);
              setPrevSearch({...prevSearch, right: value});
            }
          }
          onFocus={() => createSearchIndices('right')}
        />
      </div>
      <CardHeader
        className={classes.cardHeader}
        avatar={
          <Checkbox
            onClick={handleToggleAll(right)}
            checked={numberOfChecked(right) === right.length && right.length !== 0}
            indeterminate={numberOfChecked(right) !== right.length && numberOfChecked(right) !== 0}
            disabled={right.length === 0}
            inputProps={{ 'aria-label': 'all items selected' }}
          />
        }
        title={title}
        subheader={`${numberOfChecked(right)}/${right.length} selected`}
      >
      </CardHeader>
      <Divider />
      <FixedSizeList className={classes.list} height={300} width={300} itemSize={46} itemCount={right.length}>
        { RightRow }
      </FixedSizeList>
    </Card>
  );

  const LeftList = (title) => (
    <Card>
      <div className={classes.search}>
        <div className={classes.searchIcon}>
          <SearchIcon />
        </div>
        <InputBase
          placeholder="Search…"
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
          }}
          value={search.left}
          inputProps={{ 'aria-label': 'search' }}
          onChange={
            e => {
              const value = e.target.value;
              setSearch({...search, left: value});
              if(prevSearch.left === '' && value !== '') setTemp({ ...temp, left })
              if(value !== '') setLeft(Object.values(fuse.left.search(value)).map(item => item.item))
              else setLeft(temp.left);
              setPrevSearch({...prevSearch, left: value});
            }
          }
          onFocus={() => createSearchIndices('left')}
        />
      </div>
      <CardHeader
        className={classes.cardHeader}
        avatar={
          <Checkbox
            onClick={handleToggleAll(left)}
            checked={numberOfChecked(left) === left.length && left.length !== 0}
            indeterminate={numberOfChecked(left) !== left.length && numberOfChecked(left) !== 0}
            disabled={left.length === 0}
            inputProps={{ 'aria-label': 'all items selected' }}
          />
        }
        title={title}
        subheader={`${numberOfChecked(left)}/${left.length} selected`}
      >
      </CardHeader>
      <Divider />
      <FixedSizeList className={classes.list} height={300} width={300} itemSize={46} itemCount={left.length}>
        { LeftRow }
      </FixedSizeList>
    </Card>
  );


  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      onExited={onExited}
      fullWidth
      maxWidth='md'
      scroll='paper'
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
      <Grid container spacing={2} justify="center" alignItems="center">
        <Grid item></Grid>
        <Grid item>
        </Grid>
        <Grid item></Grid>
      </Grid>
      <Grid container spacing={2} justify="center" alignItems="center">
        <Grid item>{LeftList(leftLabel)}</Grid>
        <Grid item>
          <Grid container direction="column" alignItems="center">
            <Button
              variant="outlined"
              size="small"
              className={classes.button}
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="move selected right"
            >
              &gt;
            </Button>
            <Button
              variant="outlined"
              size="small"
              className={classes.button}
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
            >
              &lt;
            </Button>
          </Grid>
        </Grid>
        <Grid item>{RightList(rightLabel)}</Grid>
      </Grid>
      </DialogContent>  
      <DialogActions>
        <LoadingButton
          isLoading={loading}
          onClick={() => onSaveHandler({left: search.left !== '' ? temp.left : left, right: search.right !== '' ? temp.right : right})}
          color='primary'
        >
          {saveButtonText}
        </LoadingButton>
        <Button onClick={onClose} color='primary'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
    
  );
}

export default withSnackbar(EnhancferList);